import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Library as LibraryIcon, Plus, Search, X, Lightbulb, ArrowUpRight, LayoutGrid, List, FileText, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader, ErrorState, EmptyState } from "@/components/states";
import { useConfirm } from "@/context/ConfirmContext";

const STATUSES = ["draft", "recording", "analyzing", "ready", "published"];
const STATUS_LABEL = { draft: "Draft", recording: "Recording", analyzing: "Analyzing", ready: "Ready", published: "Published" };
const STATUS_COLORS = {
  draft: "bg-elevated text-muted-foreground",
  recording: "bg-blue-500/10 text-blue-500",
  analyzing: "bg-yellow-500/10 text-yellow-500",
  ready: "bg-green-500/10 text-green-600",
  published: "bg-accent/10 text-accent",
};
const PRODUCTION_LABEL = { draft: "Backlog", in_production: "In Production", ready: "Ready" };
const PLATFORMS = ["youtube", "instagram"];
const PLATFORM_LABEL = { youtube: "YouTube", instagram: "Instagram" };
const SORT_OPTIONS = [
  { value: "updated_desc", label: "Recently updated" }, { value: "created_desc", label: "Recently created" },
  { value: "created_asc", label: "Oldest" }, { value: "title_asc", label: "Title A–Z" }, { value: "title_desc", label: "Title Z–A" },
];
const formatDate = (value) => value ? new Date(value).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "—";

function ContentCard({ item, ideaTitle, onOpen, onProduction, onDelete, dense = false }) {
  const isPublished = item.status === "published" || !!item.published_at;
  const production = item.production_status || "draft";
  return (
    <div className={`group flex ${dense ? "flex-row items-start gap-4" : "flex-col"} rounded-2xl border border-border bg-surface p-5 shadow-sm transition-all hover:border-accent/30 hover:shadow-md`}
      data-testid={`library-item-${item.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display text-base font-bold text-foreground line-clamp-2 leading-snug" data-testid={`library-item-title-${item.id}`}>
            {item.title || "Untitled draft"}
          </h3>
          {isPublished && (
            <span className="shrink-0 rounded-full bg-accent/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-accent"
              data-testid={`library-item-published-${item.id}`}>Published</span>
          )}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${STATUS_COLORS[item.status] || STATUS_COLORS.draft}`}
            data-testid={`library-item-status-${item.id}`}>
            {STATUS_LABEL[item.status] || item.status}
          </span>
          <span className="rounded-full border border-accent/30 bg-accent/5 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-accent"
            data-testid={`library-item-production-${item.id}`}>
            {PRODUCTION_LABEL[production] || production}
          </span>
          {(item.platforms || []).map((platform) => (
            <span key={platform} className="rounded-full bg-elevated px-2.5 py-0.5 text-[10px] text-muted-foreground"
              data-testid={`library-item-platform-${item.id}-${platform}`}>
              {PLATFORM_LABEL[platform] || platform}
            </span>
          ))}
          {ideaTitle && (
            <span className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-0.5 text-[10px] text-muted-foreground"
              data-testid={`library-item-idea-${item.id}`}>
              <Lightbulb className="h-2.5 w-2.5" /> {ideaTitle}
            </span>
          )}
        </div>
        <div className="mt-4 flex gap-4 text-xs text-muted-foreground">
          <span data-testid={`library-item-updated-${item.id}`}>Updated {formatDate(item.updated_at || item.created_at)}</span>
        </div>
      </div>
      <div className={`${dense ? "self-center" : "mt-5"} flex shrink-0 flex-wrap items-center gap-2`}>
        <Button size="sm" variant="outline" onClick={onOpen} className="rounded-full" data-testid={`library-item-open-${item.id}`}>
          Edit <ArrowUpRight className="ml-1.5 h-3.5 w-3.5" />
        </Button>
        <Button size="sm" variant="ghost" onClick={onProduction} className="rounded-full" data-testid={`library-item-production-btn-${item.id}`}>
          Production
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={onDelete}
          className="rounded-full text-muted-foreground hover:bg-destructive/10 hover:text-destructive p-2"
          title="Delete draft"
          data-testid={`library-item-delete-${item.id}`}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

export default function Library() {
  const navigate = useNavigate();
  const { confirm } = useConfirm();
  const [state, setState] = useState({ loading: true, error: null });
  const [items, setItems] = useState([]);
  const [ideasById, setIdeasById] = useState({});
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [platformFilter, setPlatformFilter] = useState("all");
  const [sort, setSort] = useState("updated_desc");
  const [view, setView] = useState("grid");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setState({ loading: true, error: null });
    try {
      const [contentRes, ideasRes] = await Promise.all([api.get("/content"), api.get("/ideas").catch(() => ({ data: { ideas: [] } }))]);
      const idMap = {};
      for (const idea of ideasRes.data.ideas || []) { if (idea?.id) idMap[idea.id] = idea.title || "Idea"; }
      setItems(contentRes.data.content || []);
      setIdeasById(idMap);
      setState({ loading: false, error: null });
    } catch (error) { setState({ loading: false, error: apiErrorMessage(error) }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const createDraft = async () => {
    if (creating) return;
    const existingBlank = items.find(
      (item) => (item.title || "").trim().toLowerCase() === "untitled draft" && !(item.body || "").trim()
    );
    if (existingBlank) {
      toast.info("Opening existing untitled draft");
      navigate(`/content/${existingBlank.id}`);
      return;
    }

    setCreating(true);
    try {
      const { data } = await api.post("/content", { title: "Untitled draft", body: "", platforms: [] });
      navigate(`/content/${data.id}`);
    } catch (error) {
      toast.error(apiErrorMessage(error));
    } finally {
      setCreating(false);
    }
  };

  const deleteItem = async (item) => {
    const title = item.title || "Untitled draft";
    const ok = await confirm({
      title: `Delete "${title}"?`,
      description: "This item will be permanently removed from your content library.",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/content/${item.id}`);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      toast.success("Content deleted");
    } catch (error) {
      toast.error(apiErrorMessage(error));
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let result = items.filter((item) => {
      if (statusFilter !== "all" && item.status !== statusFilter) return false;
      if (platformFilter !== "all" && !(item.platforms || []).includes(platformFilter)) return false;
      if (q && !(item.title || "").toLowerCase().includes(q) && !(item.body || "").toLowerCase().includes(q)) return false;
      return true;
    });
    const byDate = (a, b, asc) => { const av = a ? new Date(a).getTime() : 0; const bv = b ? new Date(b).getTime() : 0; return asc ? av - bv : bv - av; };
    return [...result].sort((a, b) => {
      if (sort === "created_desc") return byDate(a.created_at, b.created_at, false);
      if (sort === "created_asc") return byDate(a.created_at, b.created_at, true);
      if (sort === "title_asc") return String(a.title || "").localeCompare(String(b.title || ""));
      if (sort === "title_desc") return String(b.title || "").localeCompare(String(a.title || ""));
      return byDate(a.updated_at || a.created_at, b.updated_at || b.created_at, false);
    });
  }, [items, query, statusFilter, platformFilter, sort]);

  const activeStatuses = useMemo(() => { const present = new Set(items.map((i) => i.status).filter(Boolean)); return STATUSES.filter((s) => present.has(s) || s === "draft"); }, [items]);
  const activePlatforms = useMemo(() => { const present = new Set(); for (const item of items) for (const p of item.platforms || []) present.add(p); return PLATFORMS.filter((p) => present.has(p)); }, [items]);
  const filtersActive = query.trim() !== "" || statusFilter !== "all" || platformFilter !== "all";

  return (
    <AppShell title="Library">
      <div className="mb-8 flex flex-col gap-4 border-b border-border pb-6 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-accent">Your workspace</p>
          <h1 className="mt-2 font-display text-3xl font-bold text-foreground" data-testid="library-page-title">Library</h1>
          <p className="mt-2 max-w-xl text-sm text-muted-foreground">
            Ranked by your Creator DNA and real evidence.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={() => navigate("/ideas")} className="rounded-full" data-testid="library-from-idea-button">
            <Lightbulb className="mr-2 h-4 w-4" /> From Idea
          </Button>
          <Button onClick={createDraft} disabled={creating} data-testid="library-create-button"
            className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90 shadow-sm">
            <Plus className="mr-2 h-4 w-4" /> Create Content
          </Button>
        </div>
      </div>

      {state.loading ? <Loader label="Loading your library" /> :
       state.error ? <ErrorState message={state.error} onRetry={load} /> :
       items.length === 0 ? (
        <EmptyState icon={LibraryIcon} testid="library-empty" title="No content yet"
          description="Create content from an Idea or start a blank draft."
          action={<div className="flex flex-wrap justify-center gap-2">
            <Button variant="outline" onClick={() => navigate("/ideas")} className="rounded-full" data-testid="library-empty-ideas-button"><Lightbulb className="mr-2 h-4 w-4" /> Go to Ideas</Button>
            <Button onClick={createDraft} disabled={creating} className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90" data-testid="library-empty-create-button"><Plus className="mr-2 h-4 w-4" /> Create Content</Button>
          </div>} />
       ) : (
        <>
          {/* Controls */}
          <div className="mb-6 grid grid-cols-1 gap-3 rounded-2xl border border-border bg-surface p-4 shadow-sm sm:grid-cols-2 md:grid-cols-[minmax(0,1fr)_auto_auto] lg:grid-cols-[minmax(0,1fr)_auto_auto_auto_auto]"
            data-testid="library-controls">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search title or body"
                className="pl-9 pr-9 rounded-xl" data-testid="library-search-input" aria-label="Search library" />
              {query && <button type="button" onClick={() => setQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground hover:text-foreground"
                aria-label="Clear search" data-testid="library-search-clear"><X className="h-3.5 w-3.5" /></button>}
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-auto rounded-xl text-xs" data-testid="library-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {activeStatuses.map((s) => <SelectItem key={s} value={s} data-testid={`library-status-${s}`}>{STATUS_LABEL[s]}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={platformFilter} onValueChange={setPlatformFilter}>
              <SelectTrigger className="w-full sm:w-auto rounded-xl text-xs" data-testid="library-platform-filter"><SelectValue placeholder="Platform" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All platforms</SelectItem>
                {activePlatforms.length === 0 ? <SelectItem value="__none__" disabled>No platforms yet</SelectItem> :
                 activePlatforms.map((p) => <SelectItem key={p} value={p} data-testid={`library-platform-${p}`}>{PLATFORM_LABEL[p]}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-full sm:w-auto rounded-xl text-xs" data-testid="library-sort"><SelectValue placeholder="Sort" /></SelectTrigger>
              <SelectContent>{SORT_OPTIONS.map((opt) => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}</SelectContent>
            </Select>
            <div className="flex items-center gap-1 justify-self-end rounded-xl border border-border p-1">
              <button type="button" onClick={() => setView("grid")} aria-label="Grid view" data-testid="library-view-grid"
                className={`inline-flex h-7 w-7 items-center justify-center rounded-lg ${view === "grid" ? "bg-elevated text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                <LayoutGrid className="h-3.5 w-3.5" />
              </button>
              <button type="button" onClick={() => setView("list")} aria-label="List view" data-testid="library-view-list"
                className={`inline-flex h-7 w-7 items-center justify-center rounded-lg ${view === "list" ? "bg-elevated text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                <List className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          <div className="mb-4 flex items-center justify-between text-xs text-muted-foreground" data-testid="library-summary">
            <span>Showing <span className="text-foreground">{filtered.length}</span> of <span className="text-foreground">{items.length}</span></span>
            {filtersActive && <button type="button" onClick={() => { setQuery(""); setStatusFilter("all"); setPlatformFilter("all"); }} className="text-accent hover:underline" data-testid="library-reset-filters">Reset filters</button>}
          </div>

          {filtered.length === 0 ? (
            <EmptyState icon={FileText} testid="library-no-matches" title="No content matches your search."
              description="Try clearing the search or filters."
              action={<Button variant="outline" className="rounded-full" onClick={() => { setQuery(""); setStatusFilter("all"); setPlatformFilter("all"); }} data-testid="library-clear-search">Clear filters</Button>} />
          ) : (
            <div className={view === "grid" ? "grid gap-4 md:grid-cols-2 xl:grid-cols-3" : "flex flex-col gap-3"} data-testid="library-list">
              {filtered.map((item) => (
                <ContentCard key={item.id} item={item} ideaTitle={item.idea_id ? ideasById[item.idea_id] : null}
                  onOpen={() => navigate(`/content/${item.id}`)}
                  onProduction={async () => { try { const { data } = await api.post("/production", { content_id: item.id }); navigate(`/production/${data.id}`); } catch (error) { toast.error(apiErrorMessage(error)); } }}
                  onDelete={() => deleteItem(item)}
                  dense={view === "list"} />
              ))}
            </div>
          )}
        </>
       )}
    </AppShell>
  );
}
